import { useConfirmation } from '@/hooks/useConfirmation';
import { useRef, useState, type ReactElement } from 'react';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useSubmissionWorkspace } from '@/hooks/useSubmissionWorkspace';
import { useNavigate } from 'react-router-dom';
import { v2, ApiError } from '@/api/v2Client';

/** Shared delete action; the existing endpoint enforces deletion permissions. */
export function SubmissionDeleteButton({ submissionId, name, sequence }: { submissionId: string; name?: string; sequence?: string | null }): ReactElement | null {
  const { ask, dialog } = useConfirmation();
  const navigate = useNavigate();
  const permissions = useUserPermissions();
  const workspace = useSubmissionWorkspace();
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!permissions.hasPermission('Submissions', 'delete')) return null;
  const handleDelete = async (): Promise<void> => {
    if (pending.current) return;
    if (
      !(await ask({ title: 'Delete submission', message: `Delete ${name ?? submissionId} — sequence ${sequence ?? 'unknown'}? Its documents stay but the submission disappears from the dashboard.`, confirmLabel: 'Delete', variant: 'destructive' }))
    )
      return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      await v2.submissions.delete(submissionId);
      workspace?.remove?.(submissionId);
      if (workspace) workspace.dirty.current = false;
      navigate('/home');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete submission');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return (<>
      {dialog}
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                aria-label="Delete submission"
                title="Delete submission"
                data-testid="delete-submission-btn"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 30, marginLeft: 6, background: 'rgba(255,255,255,0.08)', color: '#fecaca', border: '1px solid rgba(254,202,202,0.5)', borderRadius: 6, cursor: 'pointer' }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M3 6h18M9 6V4h6v2M5 6l1 14h12l1-14M10 10v6M14 10v6" />
                </svg>
              </button>
    {error && <span role="alert">{error}</span>}
  </>);
}
